import { useRef, useState } from "react";
import { isFileSystemAccessSupported } from "../local/fileSystemAccess";

interface Props {
  onSharePointSubmit: (url: string) => void;
  onLocalPick: () => void;
  onSinglePick: (files: File[]) => void;
  disabled?: boolean;
  sharePointEnabled?: boolean;
}

export function SourcePicker({
  onSharePointSubmit,
  onLocalPick,
  onSinglePick,
  disabled,
  sharePointEnabled = true,
}: Props) {
  const [url, setUrl] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const fsSupported = isFileSystemAccessSupported();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (url.trim() && sharePointEnabled) onSharePointSubmit(url.trim());
  };

  return (
    <div className="space-y-6">
      <div className={sharePointEnabled ? "grid md:grid-cols-2 gap-6" : "max-w-xl mx-auto"}>
        {sharePointEnabled && (
          <div className="card">
            <h2 className="text-lg font-semibold mb-2">SharePoint-Link einfügen</h2>
            <p className="text-sm text-kampmeyer-neutral-500 mb-4">
              Freigabe-Link aus OneDrive / SharePoint. Die Bilder werden in einen Unterordner
              <code className="mx-1 text-xs bg-kampmeyer-neutral-100 px-1.5 py-0.5 rounded">00_Skaliert</code>
              geschrieben.
            </p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://kampmeyer.sharepoint.com/..."
                className="w-full px-3 py-2 rounded border border-kampmeyer-neutral-300 dark:border-kampmeyer-neutral-700 bg-white dark:bg-kampmeyer-neutral-800 text-kampmeyer-neutral-900 dark:text-kampmeyer-neutral-100 placeholder:text-kampmeyer-neutral-500 focus:outline-none focus:ring-2 focus:ring-kampmeyer-primary/40 disabled:bg-kampmeyer-neutral-100 dark:disabled:bg-kampmeyer-neutral-900"
                disabled={disabled}
              />
              <button type="submit" className="btn-primary w-full" disabled={disabled || !url.trim()}>
                Ordner laden
              </button>
            </form>
          </div>
        )}

        <div className="card">
          <h2 className="text-lg font-semibold mb-2">Lokalen OneDrive-Ordner wählen</h2>
          <p className="text-sm text-kampmeyer-neutral-500 mb-4">
            Falls dein OneDrive lokal synchronisiert ist, kannst du den Ordner direkt auswählen.
            {!fsSupported && (
              <span className="block mt-2 text-amber-700">
                Diese Funktion benötigt Chrome oder Edge.
              </span>
            )}
          </p>
          <button onClick={onLocalPick} className="btn-secondary w-full" disabled={disabled || !fsSupported}>
            Ordner auswählen…
          </button>
        </div>
      </div>

      {/* Einzelbild — schneller Weg für ein vergessenes Bild */}
      <div className={sharePointEnabled ? "card" : "card max-w-xl mx-auto"}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold mb-1">Einzelnes Bild skalieren</h2>
            <p className="text-sm text-kampmeyer-neutral-500">
              Schnell ein einzelnes Bild skalieren, z. B. ein vergessenes. Das Ergebnis wird
              heruntergeladen (kein Ordner nötig).
            </p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,.heic,.heif"
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length) onSinglePick(files);
              e.target.value = "";
            }}
          />
          <button
            onClick={() => fileRef.current?.click()}
            className="btn-secondary shrink-0 whitespace-nowrap"
            disabled={disabled}
          >
            Bild auswählen…
          </button>
        </div>
      </div>
    </div>
  );
}
